/**
 * Garage Request Flow
 * Multi-step wizard for submitting feature requests with LLM maker suggestions
 */

import React, { useState, useEffect } from 'react';
import { ArrowLeft, Sparkles, Check, Clock, TrendingUp, Receipt, Shield, Heart, Rocket, Zap, Lightbulb, Gem, Smartphone } from 'lucide-react';
import garageAPI from '../../../services/garage/api';
import AlertModal from '../modals/AlertModal';
import RepoUrlGateModal from '../modals/RepoUrlGateModal';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import RequestAttachmentPicker from '../components/RequestAttachmentPicker';
import RepoAccessFields from '../components/RepoAccessFields';
import { encryptAccessTokenForTransport } from '../../../utils/secScanTokenCrypto';
import { parseStepsText } from '../../../utils/toolAppSubtype';
import { formatApiErrorDetail, parseRepoUrlGateDetail } from '../../../utils/repoUrlGate';

const GarageRequestFlow = ({ userEmail, userName, onBack, onSubmit }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [suggestedMakers, setSuggestedMakers] = useState([]);
  const [existingTools, setExistingTools] = useState([]);
  const [loadingTools, setLoadingTools] = useState(false);
  const [personaOptions, setPersonaOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [loadingPersonas, setLoadingPersonas] = useState(false);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [gitServiceUser, setGitServiceUser] = useState('');
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info'
  });
  const [repoGate, setRepoGate] = useState(null);
  const [repoGateBusy, setRepoGateBusy] = useState(false);

  useEffect(() => {
    garageAPI.getSecScanSettings()
      .then((s) => setGitServiceUser(s?.gitServiceUser || ''))
      .catch(() => setGitServiceUser(''));
  }, []);
  const [formData, setFormData] = useState({
    // Request phase: 'active-development', 'existing-enhancement', 'ideation'
    requestPhase: 'ideation',

    // App type: 'gem', 'app', null (for ideation)
    appType: null,
    // When appType is app: 'web' | 'bot'
    appSubtype: 'web',
    webUrl: '',
    rcAppUrl: '',
    steps: '',

    // Step 1: The Problem
    title: '',
    description: '',
    frequency: 'daily',
    primaryPersona: '', // NEW: Primary persona (mandatory)

    // For Gem submissions (active-development + gem)
    gemUrl: '',

    // For existing tool enhancement
    existingToolId: '',

    // For App submissions (active-development + app)
    repoUrl: '',
    repoAccessToken: '',
    deploymentPlan: '',
    technicalDetails: '',

    // Step 2: Audience
    personas: [],
    departments: [],
    reach: 'my_team',
    valueDriver: 'save_time',

    // Additional context (optional)
    additionalContext: '',
    requestedDeliveryDate: ''
  });

  const reachOptions = [
    { value: 'my_team', label: 'My team (under 10)' },
    { value: 'department', label: 'A department (10–50)' },
    { value: 'function', label: 'A function (50–500)' },
    { value: 'company', label: 'Most of the company (500+)' }
  ];

  const valueOptions = [
    { value: 'save_time', label: 'Save time', icon: 'Clock' },
    { value: 'drive_revenue', label: 'Drive revenue', icon: 'TrendingUp' },
    { value: 'reduce_cost', label: 'Reduce cost', icon: 'Receipt' },
    { value: 'reduce_risk', label: 'Reduce risk', icon: 'Shield' },
    { value: 'better_experience', label: 'Better experience', icon: 'Heart' }
  ];

  const frequencyOptions = [
    { value: 'daily', label: 'Multiple times a day' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'A few times a quarter' },
    { value: 'oneoff', label: 'One-off / Project-based' }
  ];

  const [recentRequestExamples, setRecentRequestExamples] = useState([
    "Auto-summarize Gong calls into Salesforce activity notes",
    "Calculate the cost of a customer's current PBX vs RingCX",
    "Pull together a competitive battlecard for any account by name"
  ]);

  // Load existing tools for the dropdown
  useEffect(() => {
    const loadExistingTools = async () => {
      if (formData.requestPhase === 'existing-enhancement') {
        setLoadingTools(true);
        try {
          const tools = await garageAPI.getTools();
          // Only show published tools
          const publishedTools = tools.filter(tool => tool.status === 'published');
          setExistingTools(publishedTools);
        } catch (error) {
          console.error('Failed to load tools:', error);
        } finally {
          setLoadingTools(false);
        }
      }
    };
    loadExistingTools();
  }, [formData.requestPhase]);

  // Load recent requests from the current user
  useEffect(() => {
    const loadRecentRequests = async () => {
      try {
        // Use getIdeasByUser to get only this user's requests
        const ideas = await garageAPI.getIdeasByUser(userEmail);
        console.log('📋 Loaded user ideas:', ideas.length, 'for', userEmail);

        // Get the 3 most recent request titles
        const userRequests = ideas
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 3)
          .map(idea => idea.title);

        console.log('✨ Recent request titles:', userRequests);

        if (userRequests.length > 0) {
          setRecentRequestExamples(userRequests);
        }
      } catch (error) {
        console.error('❌ Failed to load recent requests:', error);
        // Keep default examples on error
      }
    };

    if (userEmail) {
      loadRecentRequests();
    }
  }, [userEmail]);

  // Load personas + departments from database
  useEffect(() => {
    const loadAudienceOptions = async () => {
      try {
        setLoadingPersonas(true);
        const [personas, departments] = await Promise.all([
          garageAPI.getConfigOptions('persona'),
          garageAPI.getConfigOptions('department'),
        ]);
        setPersonaOptions(personas);
        setDepartmentOptions(departments || []);
      } catch (error) {
        console.error('Failed to load personas:', error);
        // Fallback to hardcoded options if API fails
        setPersonaOptions([
          { value: 'Sales', label: 'Sales' },
          { value: 'Customer Success', label: 'Customer Success' },
          { value: 'Product', label: 'Product' },
          { value: 'IT', label: 'IT' },
          { value: 'Engineering', label: 'Engineering' },
          { value: 'Marketing', label: 'Marketing' },
          { value: 'Finance', label: 'Finance' }
        ]);
        setDepartmentOptions([]);
      } finally {
        setLoadingPersonas(false);
      }
    };
    loadAudienceOptions();
  }, []);

  const togglePersona = (persona) => {
    setFormData(prev => ({
      ...prev,
      personas: prev.personas.includes(persona)
        ? prev.personas.filter(p => p !== persona)
        : [...prev.personas, persona]
    }));
  };

  const setDepartments = (departments) => {
    setFormData(prev => ({ ...prev, departments }));
  };

  const canProceed = () => {
    if (currentStep === 1) {
      // Title and primary persona are always required
      if (!formData.title.trim()) return false;
      if (!formData.primaryPersona) return false;

      // Phase-specific validation
      if (formData.requestPhase === 'active-development') {
        // Must select gem or app
        if (!formData.appType) return false;

        // Gem URL is required for Gem submissions
        if (formData.appType === 'gem' && !formData.gemUrl.trim()) return false;

        // App submissions need a repo URL (PAT optional — IT can request later)
        if (formData.appType === 'app') {
          const url = (formData.repoUrl || '').trim();
          if (!url || !/^https:\/\//i.test(url)) return false;
        }
      }

      if (formData.requestPhase === 'existing-enhancement') {
        // Must select gem or app
        if (!formData.appType) return false;

        // Must select an existing tool
        if (!formData.existingToolId) return false;
      }

      // Ideation phase only requires title and primary persona (already checked above)

      return true;
    }
    if (currentStep === 2) return true; // Step 2 is now optional (personas are additional)
    return true;
  };

  const handleNext = () => {
    if (canProceed() && currentStep < 2) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      onBack();
    }
  };

  const handleSubmit = async ({ acknowledgeExistingGarageCopy = false } = {}) => {
    try {
      setLoading(true);
      // Build detailed description from title and description
      const fullDescription = formData.description
        ? `${formData.title}\n\n${formData.description}`
        : formData.title;

      const ideaData = {
        title: formData.title,
        description: fullDescription,
        requesterEmail: userEmail,
        requesterName: userName || userEmail.split('@')[0],
        status: 'submitted',
        category: formData.primaryPersona?.toLowerCase().replace(/\s+/g, '_') || 'other',
        priority: formData.valueDriver === 'reduce_risk' ? 'high' : 'normal',
        additionalContext: formData.additionalContext,
        // Audience information
        primaryPersona: formData.primaryPersona,
        personas: formData.personas,
        departments: formData.departments,
        reach: formData.reach,
        valueDriver: formData.valueDriver,
        frequency: formData.frequency,
        requestedDeliveryDate: formData.requestedDeliveryDate || null,
        // Enhanced request submission fields
        requestPhase: formData.requestPhase,
        appType: formData.appType,
        appSubtype: formData.appType === 'app' ? (formData.appSubtype || 'web') : null,
        webUrl: formData.appType === 'app' && formData.appSubtype === 'bot'
          ? ((formData.webUrl || '').trim() || null)
          : null,
        rcAppUrl: formData.appType === 'app' && formData.appSubtype === 'bot'
          ? ((formData.rcAppUrl || '').trim() || null)
          : null,
        steps: formData.appType === 'app' && formData.appSubtype === 'bot'
          ? parseStepsText(formData.steps)
          : [],
        gemUrl: formData.requestPhase === 'active-development' && formData.appType === 'gem' ? formData.gemUrl : null,
        existingToolId: formData.requestPhase === 'existing-enhancement' ? formData.existingToolId : null,
        repoUrl: formData.requestPhase === 'active-development' && formData.appType === 'app' ? formData.repoUrl : null,
        deploymentPlan: formData.requestPhase === 'active-development' && formData.appType === 'app' ? formData.deploymentPlan : null,
        technicalDetails: formData.requestPhase === 'active-development' && formData.appType === 'app' ? formData.technicalDetails : null,
        acknowledgeExistingGarageCopy: Boolean(acknowledgeExistingGarageCopy),
      };

      if (
        ideaData.repoUrl
        && formData.requestPhase === 'active-development'
        && formData.appType === 'app'
        && (formData.repoAccessToken || '').trim()
      ) {
        ideaData.accessToken = await encryptAccessTokenForTransport(
          formData.repoAccessToken.trim()
        );
      }

      const createdIdea = await garageAPI.createIdea(ideaData);
      setRepoGate(null);

      if (attachmentFiles.length > 0) {
        try {
          await garageAPI.uploadIdeaAttachments(createdIdea.id, attachmentFiles);
        } catch (attachmentError) {
          console.error('Request created but attachment upload failed:', attachmentError);
          const detail = attachmentError?.response?.data?.detail;
          setAlertModal({
            isOpen: true,
            title: 'Request submitted',
            message: `Your request was submitted, but its files could not be uploaded${detail ? `: ${detail}` : '.'} You can add them from the request ticket.`,
            variant: 'warning',
            afterClose: onSubmit
          });
          return;
        }
      }

      if (onSubmit) {
        onSubmit();
      }
    } catch (error) {
      console.error('Failed to submit request:', error);
      const gate = parseRepoUrlGateDetail(error);
      if (gate) {
        setRepoGate(gate);
        return;
      }
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: formatApiErrorDetail(error, 'Failed to submit request. Please try again.'),
        variant: 'error'
      });
    } finally {
      setLoading(false);
      setRepoGateBusy(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return parts[0][0] + parts[1][0];
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getReachLabel = (reachValue) => {
    const option = reachOptions.find(o => o.value === reachValue);
    return option ? option.label : reachValue;
  };



  return (
    <div className="garage-request-flow">
      <div className="request-flow-container">
        {/* Left: Form Steps */}
        <div className="request-flow-main">
          {/* Header */}
          <div className="request-flow-header">
            <div className="request-progress">
              <div className="progress-step">
                <div className={`progress-dot ${currentStep >= 1 ? 'active' : ''}`}>01</div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span className={currentStep >= 1 ? 'active' : ''} style={{ fontWeight: '600' }}>The problem</span>
                  <span style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>What needs to exist?</span>
                </div>
              </div>
              <div className="progress-line" />
              <div className="progress-step">
                <div className={`progress-dot ${currentStep >= 2 ? 'active' : ''}`}>02</div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span className={currentStep >= 2 ? 'active' : ''} style={{ fontWeight: '600' }}>Audience</span>
                  <span style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>Who's it for?</span>
                </div>
              </div>
            </div>
          </div>

          {/* Step 1: The Problem */}
          {currentStep === 1 && (
            <div className="request-step">
              <div className="step-header">
                <h2>What problem are you trying to solve?</h2>
                <p className="step-subtitle">Skip the solution. Describe what's painful today.</p>
              </div>

              {/* Request Phase Selection */}
              <div className="form-group">
                <label>What type of request is this?</label>
                <div className="request-phase-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
                  <button
                    type="button"
                    className={`phase-card ${formData.requestPhase === 'active-development' ? 'selected' : ''}`}
                    onClick={() => setFormData({
                      ...formData,
                      requestPhase: 'active-development',
                      appType: null,
                      existingToolId: '',
                      gemUrl: '',
                      repoUrl: '',
                      deploymentPlan: '',
                      technicalDetails: ''
                    })}
                    style={{
                      padding: '20px',
                      border: formData.requestPhase === 'active-development' ? '2px solid #FF6B00' : '1px solid #E0E0E0',
                      borderRadius: '12px',
                      background: formData.requestPhase === 'active-development' ? '#FFF5ED' : '#fff',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ marginBottom: '12px', color: '#FF6B00' }}>
                      <Rocket size={32} />
                    </div>
                    <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '4px' }}>Submit</div>
                    <div style={{ fontSize: '13px', color: '#FF6B00', marginBottom: '8px', fontWeight: '600' }}>Active Development Phase</div>
                    <div style={{ fontSize: '13px', color: '#666' }}>In-flight project or ready for deployment</div>
                  </button>

                  <button
                    type="button"
                    className={`phase-card ${formData.requestPhase === 'existing-enhancement' ? 'selected' : ''}`}
                    onClick={() => setFormData({
                      ...formData,
                      requestPhase: 'existing-enhancement',
                      appType: null,
                      gemUrl: '',
                      repoUrl: '',
                      deploymentPlan: '',
                      technicalDetails: ''
                    })}
                    style={{
                      padding: '20px',
                      border: formData.requestPhase === 'existing-enhancement' ? '2px solid #0073E6' : '1px solid #E0E0E0',
                      borderRadius: '12px',
                      background: formData.requestPhase === 'existing-enhancement' ? '#E6F4FF' : '#fff',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ marginBottom: '12px', color: '#0073E6' }}>
                      <Zap size={32} />
                    </div>
                    <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '4px' }}>Update</div>
                    <div style={{ fontSize: '13px', color: '#0073E6', marginBottom: '8px', fontWeight: '600' }}>Feature / Optimization Request</div>
                    <div style={{ fontSize: '13px', color: '#666' }}>Maintenance, scaling, or iteration</div>
                  </button>

                  <button
                    type="button"
                    className={`phase-card ${formData.requestPhase === 'ideation' ? 'selected' : ''}`}
                    onClick={() => setFormData({
                      ...formData,
                      requestPhase: 'ideation',
                      appType: null,
                      gemUrl: '',
                      repoUrl: '',
                      deploymentPlan: '',
                      technicalDetails: '',
                      existingToolId: ''
                    })}
                    style={{
                      padding: '20px',
                      border: formData.requestPhase === 'ideation' ? '2px solid #9C27B0' : '1px solid #E0E0E0',
                      borderRadius: '12px',
                      background: formData.requestPhase === 'ideation' ? '#F3E5F5' : '#fff',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ marginBottom: '12px', color: '#9C27B0' }}>
                      <Lightbulb size={32} />
                    </div>
                    <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '4px' }}>Request</div>
                    <div style={{ fontSize: '13px', color: '#9C27B0', marginBottom: '8px', fontWeight: '600' }}>Ideation & Design Phase</div>
                    <div style={{ fontSize: '13px', color: '#666' }}>Zero code exists</div>
                  </button>
                </div>
              </div>

              {/* App Type Selection (only for active-development and existing-enhancement) */}
              {(formData.requestPhase === 'active-development' || formData.requestPhase === 'existing-enhancement') && (
                <div className="form-group">
                  <label>Is this a Gem or an App?</label>
                  <div className="chip-group">
                    <button
                      type="button"
                      className={`chip ${formData.appType === 'gem' ? 'selected' : ''}`}
                      onClick={() => setFormData({ ...formData, appType: 'gem' })}
                      style={{ padding: '12px 24px', fontSize: '16px', borderRadius: '999px', display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <Gem size={16} />
                      Gem
                    </button>
                    <button
                      type="button"
                      className={`chip ${formData.appType === 'app' ? 'selected' : ''}`}
                      onClick={() => setFormData({ ...formData, appType: 'app', appSubtype: formData.appSubtype || 'web' })}
                      style={{ padding: '12px 24px', fontSize: '16px', borderRadius: '999px', display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <Smartphone size={16} />
                      App
                    </button>
                  </div>
                </div>
              )}

              {/* App subtype (web vs bot) */}
              {(formData.requestPhase === 'active-development' || formData.requestPhase === 'existing-enhancement') && formData.appType === 'app' && (
                <div className="form-group">
                  <label>App subtype</label>
                  <div className="chip-group">
                    <button
                      type="button"
                      className={`chip ${formData.appSubtype === 'web' ? 'selected' : ''}`}
                      onClick={() => setFormData({ ...formData, appSubtype: 'web', webUrl: '', rcAppUrl: '', steps: '' })}
                      style={{ padding: '10px 20px', fontSize: '14px', borderRadius: '999px' }}
                    >
                      Web
                    </button>
                    <button
                      type="button"
                      className={`chip ${formData.appSubtype === 'bot' ? 'selected' : ''}`}
                      onClick={() => setFormData({ ...formData, appSubtype: 'bot' })}
                      style={{ padding: '10px 20px', fontSize: '14px', borderRadius: '999px' }}
                    >
                      Bot
                    </button>
                  </div>
                </div>
              )}

              {/* Optional web / RC URLs + steps — Bot subtype only */}
              {formData.requestPhase === 'active-development' && formData.appType === 'app' && formData.appSubtype === 'bot' && (
                <>
                  <div className="form-group">
                    <label>Web URL</label>
                    <p className="field-hint">Optional browser launch link</p>
                    <input
                      type="url"
                      value={formData.webUrl}
                      onChange={(e) => setFormData({ ...formData, webUrl: e.target.value })}
                      placeholder="https://..."
                      className="request-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>RC App URL</label>
                    <p className="field-hint">Optional RingCentral app launch link</p>
                    <input
                      type="url"
                      value={formData.rcAppUrl}
                      onChange={(e) => setFormData({ ...formData, rcAppUrl: e.target.value })}
                      placeholder="https://..."
                      className="request-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Steps</label>
                    <p className="field-hint">Optional how-to steps (one per line)</p>
                    <textarea
                      value={formData.steps}
                      onChange={(e) => setFormData({ ...formData, steps: e.target.value })}
                      placeholder="1. Open the bot&#10;2. ..."
                      className="request-input"
                      rows={4}
                    />
                  </div>
                </>
              )}

              {/* Gem URL Field (active-development + gem) */}
              {formData.requestPhase === 'active-development' && formData.appType === 'gem' && (
                <div className="form-group">
                  <label>Gem URL *</label>
                  <p className="field-hint">Enter the URL where your Gem is hosted</p>
                  <input
                    type="url"
                    value={formData.gemUrl}
                    onChange={(e) => setFormData({ ...formData, gemUrl: e.target.value })}
                    placeholder="e.g. https://gemini.google.com/app/..."
                    className="request-input"
                    required
                  />
                </div>
              )}

              {/* Existing Tool Selector (existing-enhancement + (gem or app)) */}
              {formData.requestPhase === 'existing-enhancement' && formData.appType && (
                <div className="form-group">
                  <label>Select the {formData.appType} to enhance</label>
                  <p className="field-hint">Choose from published {formData.appType}s</p>
                  {loadingTools ? (
                    <div style={{ padding: '10px', color: '#666' }}>Loading tools...</div>
                  ) : (
                    <select
                      value={formData.existingToolId}
                      onChange={(e) => setFormData({ ...formData, existingToolId: e.target.value })}
                      className="request-input"
                      required
                    >
                      <option value="">-- Select a {formData.appType} --</option>
                      {existingTools
                        .filter(tool => tool.toolType && tool.toolType.toLowerCase() === formData.appType)
                        .map(tool => (
                          <option key={tool.id} value={tool.id}>
                            {tool.title}
                          </option>
                        ))}
                    </select>
                  )}
                </div>
              )}

              <div className="form-group">
                <label>
                  Primary Persona <span style={{color: 'var(--garage-red)', marginLeft: '4px'}}>*</span>
                </label>
                <p className="field-hint">Who is the primary audience for this request?</p>
                <select
                  value={formData.primaryPersona}
                  onChange={(e) => setFormData({ ...formData, primaryPersona: e.target.value })}
                  className="request-input"
                  required
                  disabled={loadingPersonas}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid var(--garage-border)',
                    backgroundColor: 'var(--garage-surface)',
                    color: 'var(--garage-text-1)',
                    fontSize: '14px',
                    width: '100%',
                    cursor: loadingPersonas ? 'wait' : 'pointer'
                  }}
                >
                  <option value="">
                    {loadingPersonas ? 'Loading personas...' : 'Select a persona...'}
                  </option>
                  {personaOptions.map(persona => (
                    <option key={persona.value} value={persona.value}>
                      {persona.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Request summary</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g. I waste 20 minutes building the same QBR slide every week"
                  maxLength={120}
                  className="request-input"
                />
                <div className="char-counter">{formData.title.length} / 120</div>
              </div>

              <div className="form-group">
                <label>Additional details</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What do you do today? Why is it slow or annoying? What would the ideal experience look like?"
                  rows={5}
                  className="request-textarea"
                />
              </div>

              <div className="form-group">
                <label>Requested delivery date <span className="garage-optional">(Optional)</span></label>
                <p className="field-hint">Used if this request is later escalated to IT BizOps.</p>
                <input
                  type="date"
                  value={formData.requestedDeliveryDate}
                  onChange={(e) => setFormData({ ...formData, requestedDeliveryDate: e.target.value })}
                  className="request-input"
                />
              </div>

              <div className="form-group">
                <label>How often does this happen?</label>
                <p className="field-hint">Helps makers prioritize.</p>
                <div className="chip-group">
                  {frequencyOptions.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      className={`chip ${formData.frequency === option.value ? 'selected' : ''}`}
                      onClick={() => setFormData({ ...formData, frequency: option.value })}
                      style={{ borderRadius: '999px', WebkitTapHighlightColor: 'transparent' }}
                      onMouseDown={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseUp={(e) => e.currentTarget.style.opacity = '1'}
                      onTouchStart={(e) => e.currentTarget.style.opacity = '1'}
                      onTouchEnd={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* App Submission Fields (active-development + app) */}
              {formData.requestPhase === 'active-development' && formData.appType === 'app' && (
                <>
                  <div className="form-group">
                    <label>
                      Repository URL <span className="required">*</span>
                    </label>
                    <p className="field-hint">
                      HTTPS GitLab/GitHub link. Required so IT can run scans after Route to IT.
                    </p>
                    <input
                      type="url"
                      value={formData.repoUrl}
                      onChange={(e) => setFormData({ ...formData, repoUrl: e.target.value })}
                      placeholder="e.g. https://git.ringcentral.com/org/repo.git"
                      className="request-input"
                      required
                    />
                  </div>

                  <RepoAccessFields
                    idPrefix="submit-app"
                    serviceUser={gitServiceUser}
                    accessToken={formData.repoAccessToken}
                    onAccessTokenChange={(v) => setFormData({ ...formData, repoAccessToken: v })}
                    disabled={loading}
                    hintExtra="Provide a token now, or leave blank — IT can ask you for it on the Initiated step."
                  />

                  <div className="form-group">
                    <label>Deployment Plan / Instructions (optional)</label>
                    <p className="field-hint">How should this tool be deployed? Any specific requirements?</p>
                    <textarea
                      value={formData.deploymentPlan}
                      onChange={(e) => setFormData({ ...formData, deploymentPlan: e.target.value })}
                      placeholder="e.g. Deploy as a web app, needs API keys for X and Y, requires database..."
                      rows={4}
                      className="request-textarea"
                    />
                  </div>

                  <div className="form-group">
                    <label>Technical Details (optional)</label>
                    <p className="field-hint">Tech stack, dependencies, integrations, etc.</p>
                    <textarea
                      value={formData.technicalDetails}
                      onChange={(e) => setFormData({ ...formData, technicalDetails: e.target.value })}
                      placeholder="e.g. Python/FastAPI backend, React frontend, integrates with Salesforce API..."
                      rows={3}
                      className="request-textarea"
                    />
                  </div>
                </>
              )}

              <div className="recent-requests-section">
                <p className="recent-requests-label">FOR REFERENCE — RECENT REQUESTS</p>
                {recentRequestExamples.map((example, index) => (
                  <div key={index} className="recent-request-example">
                    "{example}"
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Audience */}
          {currentStep === 2 && (
            <div className="request-step">
              <div className="step-header">
                <h2>Who is this for?</h2>
                <p className="step-subtitle">Pick all that apply. The wider the audience, the higher the priority.</p>
              </div>

              <div className="form-group">
                <label>Additional Personas (Optional)</label>
                <p className="field-hint">
                  Primary: <strong>{formData.primaryPersona || 'Not selected'}</strong> — Select additional audiences if applicable.
                </p>
                <div className="chip-group">
                  {personaOptions.map(persona => (
                    <button
                      key={persona.value}
                      type="button"
                      className={`chip ${formData.personas.includes(persona.label) ? 'selected' : ''}`}
                      onClick={() => togglePersona(persona.label)}
                    >
                      {persona.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                {departmentOptions.length === 0 ? (
                  <>
                    <label>Target Departments (Optional)</label>
                    <p className="field-hint">No departments configured yet.</p>
                  </>
                ) : (
                  <MultiSelectDropdown
                    label="Target Departments (Optional)"
                    options={departmentOptions}
                    value={formData.departments}
                    onChange={setDepartments}
                    placeholder="Select departments..."
                    helpText="Used for Discover Mode B access when this request is published."
                  />
                )}
              </div>

              <div className="form-group">
                <label>Attachments (Optional)</label>
                <p className="field-hint">
                  Add supporting documents or sample files. Files stay private to this request.
                </p>
                <RequestAttachmentPicker
                  files={attachmentFiles}
                  onChange={setAttachmentFiles}
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Roughly how many people would use this?</label>
                <p className="field-hint">Honest guess is fine.</p>
                <div className="chip-group">
                  {reachOptions.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      className={`chip ${formData.reach === option.value ? 'selected' : ''}`}
                      onClick={() => setFormData({ ...formData, reach: option.value })}
                      style={{ borderRadius: '999px' }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>What's the rough business value?</label>
                <p className="field-hint">For prioritization.</p>
                <div className="chip-group">
                  {valueOptions.map(option => {
                    const IconComponent = {
                      Clock,
                      TrendingUp,
                      Receipt,
                      Shield,
                      Heart
                    }[option.icon];

                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={`chip ${formData.valueDriver === option.value ? 'selected' : ''}`}
                        onClick={() => setFormData({ ...formData, valueDriver: option.value })}
                        style={{ borderRadius: '999px' }}
                      >
                        {IconComponent && <IconComponent size={12} style={{ marginRight: '4px' }} />}
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons with Back navigation */}
          <div className="request-actions">
            <button
              onClick={handleBack}
              className="garage-btn garage-btn-secondary"
              style={{ WebkitTapHighlightColor: 'transparent' }}
              onMouseDown={(e) => e.currentTarget.style.opacity = '1'}
              onMouseUp={(e) => e.currentTarget.style.opacity = '1'}
            >
              {currentStep === 1 ? 'Cancel' : 'Back'}
            </button>
            {currentStep < 2 ? (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className="garage-btn garage-btn-primary"
                style={{ WebkitTapHighlightColor: 'transparent' }}
                onMouseDown={(e) => e.currentTarget.style.opacity = '1'}
                onMouseUp={(e) => e.currentTarget.style.opacity = '1'}
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="garage-btn garage-btn-primary"
                style={{ WebkitTapHighlightColor: 'transparent' }}
                onMouseDown={(e) => e.currentTarget.style.opacity = '1'}
                onMouseUp={(e) => e.currentTarget.style.opacity = '1'}
              >
                {loading ? 'Submitting...' : 'Submit request'}
              </button>
            )}
          </div>
        </div>

        {/* Right: Summary Sidebar */}
        <div className="request-flow-sidebar">
          <div className="request-summary">
            <h3>SUMMARY</h3>

            <div className="summary-item">
              <label>Request Phase</label>
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {formData.requestPhase === 'active-development' && (
                  <>
                    <Rocket size={14} />
                    Active Development
                  </>
                )}
                {formData.requestPhase === 'existing-enhancement' && (
                  <>
                    <Zap size={14} />
                    Enhancement
                  </>
                )}
                {formData.requestPhase === 'ideation' && (
                  <>
                    <Lightbulb size={14} />
                    Ideation
                  </>
                )}
              </span>
            </div>

            {formData.appType && (
              <div className="summary-item">
                <label>Type</label>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {formData.appType === 'gem' ? (
                    <>
                      <Gem size={14} />
                      Gem
                    </>
                  ) : (
                    <>
                      <Smartphone size={14} />
                      App
                    </>
                  )}
                </span>
              </div>
            )}

            {formData.requestPhase === 'existing-enhancement' && formData.existingToolId && (
              <div className="summary-item">
                <label>Tool</label>
                <span>{existingTools.find(t => t.id === formData.existingToolId)?.title || '—'}</span>
              </div>
            )}

            {formData.requestPhase === 'active-development' && formData.appType === 'gem' && formData.gemUrl && (
              <div className="summary-item">
                <label>Gem URL</label>
                <span style={{ fontSize: '12px', wordBreak: 'break-all' }}>{formData.gemUrl}</span>
              </div>
            )}

            <div className="summary-item">
              <label>Summary</label>
              <span>{formData.title || '—'}</span>
            </div>

            <div className="summary-item">
              <label>Frequency</label>
              <span>{frequencyOptions.find(o => o.value === formData.frequency)?.label || formData.frequency}</span>
            </div>

            <div className="summary-divider" />

            <div className="summary-item">
              <label>Personas</label>
              <span>{formData.personas.length > 0 ? formData.personas.join(', ') : '—'}</span>
            </div>

            <div className="summary-item">
              <label>Departments</label>
              <span>
                {formData.departments.length > 0
                  ? formData.departments
                      .map((v) => departmentOptions.find((d) => d.value === v)?.label || v)
                      .join(', ')
                  : '—'}
              </span>
            </div>

            <div className="summary-item">
              <label>Attachments</label>
              <span>{attachmentFiles.length > 0 ? `${attachmentFiles.length} selected` : '—'}</span>
            </div>

            <div className="summary-item">
              <label>Reach</label>
              <span>{getReachLabel(formData.reach)}</span>
            </div>

            <div className="summary-item">
              <label>Value</label>
              <span>{valueOptions.find(o => o.value === formData.valueDriver)?.label || formData.valueDriver}</span>
            </div>
          </div>
        </div>
      </div>

      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        onClose={() => {
          const afterClose = alertModal.afterClose;
          setAlertModal({ ...alertModal, isOpen: false, afterClose: null });
          if (afterClose) afterClose();
        }}
      />
      <RepoUrlGateModal
        isOpen={Boolean(repoGate)}
        gate={repoGate}
        acknowledging={repoGateBusy}
        onCancel={() => setRepoGate(null)}
        onUseCopyUrl={(url) => {
          if (url) {
            setFormData((prev) => ({ ...prev, repoUrl: url }));
          }
          setRepoGate(null);
        }}
        onAcknowledge={async () => {
          setRepoGateBusy(true);
          await handleSubmit({ acknowledgeExistingGarageCopy: true });
        }}
      />
    </div>
  );
};

export default GarageRequestFlow;
